import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, CheckCircle, Upload, MessageCircle, Star, Briefcase, GraduationCap, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero } from '../components/PageComponents';
import { kandidaatVoordelenData } from '../data/extraMockData';
import { faqData } from '../data/mockData';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay }} className={className}>
      {children}
    </motion.div>
  );
};

const icons = [Star, Briefcase, GraduationCap, CheckCircle, Users, MessageCircle];

const VoorKandidatenPage = () => {
  return (
    <main className="pt-28 min-h-screen">
      <SEO title="Voor Kandidaten | Finance & HR vacatures - Staffing Force" description="Op zoek naar een nieuwe uitdaging in Finance of HR? Staffing Force helpt professionals aan interim opdrachten, detavast en vaste posities bij toonaangevende organisaties." />

      <PageHero
        label="Voor professionals"
        title="Voor Kandidaten"
        description="Op zoek naar een nieuwe uitdaging in Finance of HR? Wij helpen u aan de perfecte opdracht of vaste positie."
        image="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&q=85&auto=format&fit=crop"
      />

      {/* Steps */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Hoe het werkt</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-3">In 4 stappen aan de slag</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-4 gap-8">
            {[{ step: '01', title: 'Aanmelden', desc: 'Schrijf je in via onze website of stuur je CV.' },
              { step: '02', title: 'Kennismaken', desc: 'We plannen een persoonlijk gesprek om je wensen te bespreken.' },
              { step: '03', title: 'Matchen', desc: 'We zoeken de perfecte match bij onze opdrachtgevers.' },
              { step: '04', title: 'Aan de slag', desc: 'Je start met je opdracht met volledige begeleiding.' }].map((s, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="relative">
                  <span className="text-6xl font-black text-[#00897B]/10">{s.step}</span>
                  <h3 className="text-lg font-bold text-gray-900 -mt-4">{s.title}</h3>
                  <p className="text-gray-600 text-sm mt-2">{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Waarom Staffing Force</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-3">Wat wij u bieden</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {kandidaatVoordelenData.map((v, i) => {
              const Icon = icons[i] || CheckCircle;
              return (
                <FadeIn key={i} delay={i * 0.08}>
                  <div className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow h-full">
                    <Icon size={24} className="text-[#00897B] mb-4" />
                    <h3 className="font-bold text-gray-900 mb-2">{v.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{v.description}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Veelgestelde vragen</span>
              <h2 className="text-3xl font-bold text-gray-900 mt-3">FAQ voor kandidaten</h2>
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <Accordion type="single" collapsible className="space-y-3">
              {faqData.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-gray-50 border border-gray-100 rounded-xl px-6 overflow-hidden data-[state=open]:bg-[#00897B]/5 data-[state=open]:border-[#00897B]/20 transition-colors">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#00897B] py-5 text-sm hover:no-underline">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed pb-5 text-sm">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#00897B]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Klaar voor uw volgende stap?</h2>
            <p className="text-white/80 mb-8">Bekijk onze openstaande vacatures of neem direct contact op.</p>
            <div className="flex gap-4 justify-center">
              <Link to="/vacatures" className="bg-white text-[#00897B] px-8 py-3 rounded font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg">Bekijk vacatures</Link>
              <Link to="/contact" className="border-2 border-white/50 hover:border-white text-white px-8 py-3 rounded font-semibold text-sm transition-all hover:-translate-y-0.5">Neem contact op</Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default VoorKandidatenPage;
